import React, { useEffect, useRef, useState } from 'react';
import Papa from 'papaparse';
import toast from 'react-hot-toast';
import { AccountsReportCsvDownloadButton } from '../components/AccountsReportCsvDownloadButton';
import { useAccountsReportToolbar } from '../contexts/AccountsReportToolbarContext';
import { Field, StatusPill } from '../../shared/components/ui/primitives';
import { useProjectsList } from '../../../contexts/ProjectsContext';
import { filterInput } from './PerformaInvoiceList';

// Shared by Paid Bills and Pending Bills: the kinds of payee, the filters
// both pages have, and the CSV button in the Accounts Report tab row.

export const KIND_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'material', label: 'Material (vendor PIs)' },
  { value: 'service_providers', label: 'Service providers' },
  { value: 'labour', label: 'Labour wages (Operations)' },
  { value: 'site', label: 'Site expenses (wallet)' }
];

const KIND_PILL = {
  material: { tone: 'info', label: 'Material' },
  service_providers: { tone: 'brand', label: 'Service provider' },
  labour: { tone: 'neutral', label: 'Labour wages' },
  site: { tone: 'warning', label: 'Site wallet' }
};

export const KindPill = ({ kind }) => {
  const pill = KIND_PILL[kind] ?? { tone: 'neutral', label: kind };
  return (
    <StatusPill tone={pill.tone} dense>
      {pill.label}
    </StatusPill>
  );
};

export const KIND_TEXT = Object.fromEntries(
  Object.entries(KIND_PILL).map(([k, v]) => [k, v.label])
);

/** Project, type and a search box that waits for typing to stop. */
export function useBillsFilters(extra = {}) {
  const empty = { project_id: '', kind: '', search: '', ...extra };
  const [filters, setFilters] = useState(empty);
  const [typed, setTyped] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => ({ ...f, search: typed.trim() })), 350);
    return () => clearTimeout(t);
  }, [typed]);
  const set = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));
  const filtered = typed || Object.entries(filters).some(([k, v]) => k !== 'search' && v);
  const clear = () => {
    setTyped('');
    setFilters(empty);
  };
  return { filters, setFilters, typed, setTyped, set, filtered, clear };
}

export const BillsFilterFields = ({ filters, set, typed, setTyped, placeholder }) => {
  const { projects = [] } = useProjectsList();
  return (
    <>
      <Field label="Search" size="caption">
        <input
          type="search"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={placeholder}
          className={`${filterInput} w-56`}
        />
      </Field>
      <Field label="Project" size="caption">
        <select value={filters.project_id} onChange={set('project_id')} className={filterInput}>
          <option value="">All projects</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Type" size="caption">
        <select value={filters.kind} onChange={set('kind')} className={filterInput}>
          {KIND_OPTIONS.map((k) => (
            <option key={k.value} value={k.value}>
              {k.label}
            </option>
          ))}
        </select>
      </Field>
    </>
  );
};

/**
 * Puts the CSV button in the Accounts Report tab row. `fetchAll` returns every
 * row for the current filters; `toLine` turns one into a CSV line.
 */
export function useCsvButton({ fetchAll, toLine, fileName }) {
  const { setTabEndAction } = useAccountsReportToolbar();
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const rows = await fetchAll();
      if (!rows.length) {
        toast.error('Nothing to download for these filters.');
        return;
      }
      const blob = new Blob([Papa.unparse(rows.map(toLine))], { type: 'text/csv;charset=utf-8;' });
      const href = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = href;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(href);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not download the CSV');
    } finally {
      setBusy(false);
    }
  };
  const runRef = useRef(run);
  runRef.current = run;
  useEffect(() => {
    setTabEndAction(
      <AccountsReportCsvDownloadButton
        onClick={() => runRef.current()}
        disabled={busy}
        statusText={busy ? 'Downloading…' : null}
      />
    );
    return () => setTabEndAction(null);
  }, [setTabEndAction, busy]);
}
